/**
 *
 * WCCMultiBUOfferPriceBreakdown
 *
 */

import React from "react";
import { IntlPropType, GetCountryCode } from "root/libs/core-libs/src";
import {
  showCurrencyAsCode,
  getCurrencyFractionDigitsOptions,
} from "root/libs/constants/src";
import PropTypes from "prop-types";
import { DhlText } from "@dhl-official/react-library";

import {
  PriceBreakdownSegmentWrapper,
  PriceBreakdownHeadlineWrapper,
  PriceBreakdownWrapper,
  StyledHeadline,
  StyledLabels,
  StyledValues,
  StyledLabelText,
  FullWidthTable,
  EuroPriceSpan,
  ExchangeRateCell,
  PriceContainer,
} from "./styled";

const COPY_ID_PREFIX = "WCCMultiBUOfferPriceBreakdown";
export default class WCCMultiBUOfferPriceBreakdown extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string,
    offerIndex: PropTypes.any,
    /** An object that contains all the information required of an offer */
    offer: PropTypes.shape({
      businessUnit: PropTypes.string,
      /** id of the offer */
      key: PropTypes.string,
      id: PropTypes.string,
      /** Price of the offer */
      price: PropTypes.shape({
        /** A number that contains the price
         */
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** A valid currency code */
        currencyCode: PropTypes.string,
        tax: PropTypes.string,
        billingAmount: PropTypes.number,
        billingTaxAmount: PropTypes.number,
        /** Conversion factor for currency */
        currencyConversionFactor: PropTypes.number,
      }),
      /** Euro price of the offer */
      euroPrice: PropTypes.shape({
        /** A number that contains the euro price */
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** A valid currency code */
        currencyCode: PropTypes.string,
        tax: PropTypes.string,
        /** Conversion factor for currency */
        currencyConversionFactor: PropTypes.number,
      }),

      /** If set will trigger the display of a special scenario card in DGF */
      specialOffers: PropTypes.string,
    }).isRequired,
    /** Internationalization library */
    intl: IntlPropType.isRequired,
    showCurrencyIsoCodeForPrices: PropTypes.bool,
    nativeLanguageCode: PropTypes.string,
    formatCurrencyWithNativeLocale: PropTypes.bool,
    getRef: PropTypes.func,
  };

  static defaultProps = {
    showCurrencyIsoCodeForPrices: false,
    nativeLanguageCode: "",
    formatCurrencyWithNativeLocale: false,
  };

  constructor(props) {
    super(props);
    this.priceBreakdownRef = React.createRef();
  }

  componentDidMount() {
    const { getRef } = this.props;
    if (getRef) {
      getRef(this.priceBreakdownRef);
    }
  }

  getCopy = (id) => {
    const { intl } = this.props;
    return intl.formatMessage({ id });
  };

  getOfferPrice = () => {
    const { offer } = this.props;

    if (!("price" in offer)) {
      return null;
    }

    const offerNet = offer.price.billingAmount || 0;
    const offerTax = offer.price.billingTaxAmount || 0;

    const offerGross = offerNet + offerTax;

    return { offerNet, offerTax, offerGross };
  };

  getOfferCurrency = () => {
    const { offer } = this.props;

    if (!("price" in offer)) {
      return null;
    }

    if (typeof offer.price.currencyCode !== "undefined") {
      return offer.price.currencyCode;
    }
    return null;
  };

  // eslint-disable-next-line consistent-return
  formatPrice = (value, currency = null) => {
    const {
      intl,
      showCurrencyIsoCodeForPrices,
      nativeLanguageCode,
      formatCurrencyWithNativeLocale,
    } = this.props;

    const currencyCode = currency || this.getOfferCurrency();
    const countryCode = GetCountryCode(intl.locale);
    const priceFormatLocale = formatCurrencyWithNativeLocale
      ? `${nativeLanguageCode}-${countryCode}`
      : intl.locale;
    const isCurrencyCodeAvailable =
      currencyCode !== "" && currencyCode !== null;

    if (showCurrencyIsoCodeForPrices && isCurrencyCodeAvailable) {
      return `${currencyCode} ${intl.formatNumber(
        value,
        getCurrencyFractionDigitsOptions(currencyCode)
      )}`;
    }
    if (!showCurrencyIsoCodeForPrices && isCurrencyCodeAvailable) {
      return new Intl.NumberFormat(priceFormatLocale, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: showCurrencyAsCode.includes(currencyCode)
          ? "code"
          : "symbol",
      }).format(value);
    }
    if (currencyCode === "") {
      return intl.formatNumber(value, {
        minimumFractionDigits: 2,
      });
    }
  };

  getEuroPrice = () => {
    const { offer } = this.props;
    return offer?.euroPrice?.amount > 0
      ? offer.euroPrice.amount
      : offer?.euroPrice?.amount || null;
  };

  getEuroCurrency = () => {
    const { offer } = this.props;
    return offer?.euroPrice?.currencyCode || null;
  };

  getExchangeRate = () => {
    const { offer } = this.props;
    const conversionFactor = offer?.price?.currencyConversionFactor;
    return conversionFactor && !Number.isNaN(Number(conversionFactor))
      ? Number(conversionFactor)
      : null;
  };

  getEuroPriceBreakdown = () => {
    const { offerNet, offerTax, offerGross } = this.getOfferPrice();
    const euroPrice = this.getEuroPrice();
    const exchangeRate = this.getExchangeRate();
    if (euroPrice === null || offerGross === 0) {
      return { euroNet: null, euroTax: null, euroGross: euroPrice };
    }

    const euroNet = offerNet / exchangeRate;
    const euroTax = offerTax / exchangeRate;
    const euroGross = offerGross / exchangeRate;

    return {
      euroNet,
      euroTax,
      euroGross,
    };
  };

  render() {
    const { id, offerIndex, intl, offer } = this.props;
    const TITLE = intl.formatMessage({ id: `${COPY_ID_PREFIX}.Title` });
    const NETTEXT = intl.formatMessage({ id: `${COPY_ID_PREFIX}.NET` });
    const VATTEXT = intl.formatMessage({ id: `${COPY_ID_PREFIX}.VAT` });
    const GROSSTEXT = intl.formatMessage({ id: `${COPY_ID_PREFIX}.GROSS` });
    const EXCHANGERATE = intl.formatMessage({
      id: `${COPY_ID_PREFIX}.EXCHANGE_RATE`,
    });

    const { offerNet, offerTax, offerGross } = this.getOfferPrice();
    const { euroNet, euroTax, euroGross } = this.getEuroPriceBreakdown();

    const currencyCode = this.getOfferCurrency();
    const euroCurrencyCode = this.getEuroCurrency();
    const exchangeRate = this.getExchangeRate();
    const hasEuroPrice = euroGross !== null && euroCurrencyCode !== null;

    // If the offer tax is 0, do not display the price breakdown,
    // since the net and gross prices would be the same,
    // and there would not be anything to break down.
    if (offerTax === 0) {
      return null;
    }

    return (
      <PriceBreakdownSegmentWrapper
        id={`${id}-${offer?.key}`}
        ref={this.priceBreakdownRef}
      >
        <PriceBreakdownHeadlineWrapper>
          <StyledHeadline
            color="var(--dui-color-red-500)"
            dataTestid={`${id}-headline-${offerIndex}`}
            id={`${id}-headline-${offerIndex}`}
            priority={3}
          >
            {TITLE}
          </StyledHeadline>
        </PriceBreakdownHeadlineWrapper>
        <PriceBreakdownWrapper>
          <FullWidthTable hasEuroPrice={hasEuroPrice}>
            <tbody>
              <tr>
                <StyledLabels scope="row" hasEuroPrice={hasEuroPrice}>
                  <StyledLabelText
                    id={`${id}-net-${offerIndex}-label`}
                    dataTestid={`${id}-net-label-${offerIndex}`}
                  >
                    {NETTEXT}
                  </StyledLabelText>
                </StyledLabels>
                <StyledValues>
                  <DhlText
                    id={`${id}-net-${offerIndex}-value`}
                    dataTestid={`${id}-net-value-${offerIndex}`}
                  >
                    <PriceContainer>
                      {this.formatPrice(offerNet)}
                    </PriceContainer>
                    {hasEuroPrice && (
                      <EuroPriceSpan>
                        {`(${this.formatPrice(euroNet, euroCurrencyCode)})`}
                      </EuroPriceSpan>
                    )}
                  </DhlText>
                </StyledValues>
              </tr>
              <React.Fragment key={`${id}-vat-${offerIndex}`}>
                <tr>
                  <StyledLabels scope="row" hasEuroPrice={hasEuroPrice}>
                    <StyledLabelText
                      id={`${id}-vat-${offerIndex}-label`}
                      dataTestid={`${id}-vat-label-${offerIndex}`}
                    >
                      {VATTEXT}
                    </StyledLabelText>
                  </StyledLabels>
                  <StyledValues>
                    <DhlText
                      id={`${id}-vat-${offerIndex}-value`}
                      dataTestid={`${id}-vat-value-${offerIndex}`}
                    >
                      <PriceContainer>
                        {this.formatPrice(offerTax)}
                      </PriceContainer>
                      {hasEuroPrice && (
                        <EuroPriceSpan>
                          {`(${this.formatPrice(euroTax, euroCurrencyCode)})`}
                        </EuroPriceSpan>
                      )}
                    </DhlText>
                  </StyledValues>
                </tr>

                <tr>
                  <StyledLabels scope="row" hasEuroPrice={hasEuroPrice}>
                    <StyledLabelText
                      id={`${id}-gross-${offerIndex}-label`}
                      dataTestid={`${id}-gross-label-${offerIndex}`}
                    >
                      {GROSSTEXT}
                    </StyledLabelText>
                  </StyledLabels>
                  <StyledValues>
                    <DhlText
                      id={`${id}-gross-${offerIndex}-value`}
                      dataTestid={`${id}-gross-value-${offerIndex}`}
                    >
                      <PriceContainer>
                        {this.formatPrice(offerGross)}
                      </PriceContainer>
                      {hasEuroPrice && (
                        <EuroPriceSpan>
                          {`(${this.formatPrice(euroGross, euroCurrencyCode)})`}
                        </EuroPriceSpan>
                      )}
                    </DhlText>
                  </StyledValues>
                  {hasEuroPrice && exchangeRate && (
                    <ExchangeRateCell>
                      <DhlText
                        id={`${id}-exchange-rate-${offerIndex}`}
                        dataTestid={`${id}-exchange-rate-${offerIndex}`}
                      >
                        {EXCHANGERATE} 1 {euroCurrencyCode} ={" "}
                        {exchangeRate.toFixed(5)} {currencyCode}
                      </DhlText>
                    </ExchangeRateCell>
                  )}
                </tr>
              </React.Fragment>
            </tbody>
          </FullWidthTable>
        </PriceBreakdownWrapper>
      </PriceBreakdownSegmentWrapper>
    );
  }
}
